import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Grid,
  Icon,
  IconButton,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import StatusBadge from "components/StatusBadge";
import MonoText from "components/MonoText";
import EmptyState from "components/EmptyTable/EmptyState";
import PearlLogoLoader from "components/PearlLogoLoader";
import ConfirmDeleteModal from "components/ConfirmDeleteModal";
import NewPaymentTransactionModal from "components/NewPaymentTransactionModal";
import { useAuth } from "auth/Auth";
import { usePermission } from "auth/usePermission";

const formatAmount = (amount) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(amount || 0);

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

function ActionMenu({ row, onView, onDelete }) {
  const [anchorEl, setAnchorEl] = useState(null);

  return (
    <>
      <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {onView && (
          <MenuItem
            onClick={() => {
              onView(row);
              setAnchorEl(null);
            }}
          >
            <ListItemIcon>
              <VisibilityIcon fontSize="small" />
            </ListItemIcon>
            View
          </MenuItem>
        )}
        {onDelete && (
          <MenuItem
            onClick={() => {
              onDelete(row.id);
              setAnchorEl(null);
            }}
            sx={{ color: "error.main" }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" sx={{ color: "error.main" }} />
            </ListItemIcon>
            Delete
          </MenuItem>
        )}
      </Menu>
    </>
  );
}

const PaymentTransactions = () => {
  const { axiosInstance } = useAuth();
  const navigate = useNavigate();
  const { canCreate } = usePermission('payment_transactions');

  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null });

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get("/payment-transactions/");
      setTransactions(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching payment transactions:", error);
      setSnackbar({ open: true, message: "Error loading payment transactions", severity: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveTransaction = async (payload) => {
    try {
      await axiosInstance.post("/payment-transactions/", payload);
      setSnackbar({ open: true, message: "Payment Transaction created successfully", severity: "success" });
      fetchTransactions();
    } catch (error) {
      console.error("Error saving payment transaction:", error);
      throw error;
    }
  };

  const handleView = (row) => {
    navigate(`/payment-transaction/${row.id}`, { state: { name: row.name } });
  };

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`/payment-transactions/${deleteConfirm.id}`);
      setSnackbar({ open: true, message: "Payment Transaction deleted successfully", severity: "success" });
      fetchTransactions();
    } catch (error) {
      console.error("Error deleting payment transaction:", error);
      setSnackbar({ open: true, message: "Error deleting payment transaction", severity: "error" });
      throw error;
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
        width: "14%",
        Cell: ({ value, row }) => (
          <MDTypography
            component="span"
            variant="caption"
            color="info"
            fontWeight="medium"
            sx={{ cursor: "pointer" }}
            onClick={() => handleView(row.original)}
          >
            {value || "—"}
          </MDTypography>
        ),
      },
      {
        Header: "Order",
        accessor: "travel_order_id",
        width: "12%",
        Cell: ({ row }) => (
          <MonoText>
            {row.original.travel_order?.name || row.original.travel_order_id || "—"}
          </MonoText>
        ),
      },
      {
        Header: "Invoice",
        accessor: "invoice_id",
        width: "12%",
        Cell: ({ row }) => (
          <MonoText>
            {row.original.invoice?.invoice_number || row.original.invoice_id || "—"}
          </MonoText>
        ),
      },
      {
        Header: "Payment Date",
        accessor: "payment_date",
        width: "12%",
        Cell: ({ value }) => <MonoText>{formatDate(value)}</MonoText>,
      },
      {
        Header: "Amount",
        accessor: "amount",
        width: "12%",
        Cell: ({ value }) => (
          <MDTypography variant="caption" fontWeight="bold">
            {formatAmount(value)}
          </MDTypography>
        ),
      },
      {
        Header: "Method",
        accessor: "payment_method",
        width: "10%",
        Cell: ({ value }) => value || "—",
      },
      {
        Header: "Mode",
        accessor: "payment_mode",
        width: "10%",
        Cell: ({ value }) => value || "—",
      },
      {
        Header: "Status",
        accessor: "payment_status",
        width: "12%",
        Cell: ({ value }) => <StatusBadge status={value || "Payment Pending"} label={value || "Payment Pending"} />,
      },
      {
        Header: "Action",
        accessor: "actions",
        width: "6%",
        disableSortBy: true,
        Cell: ({ row }) => (
          <ActionMenu
            row={row.original}
            onView={handleView}
            onDelete={(id) => setDeleteConfirm({ open: true, id })}
          />
        ),
      },
    ],
    []
  );

  const tableData = useMemo(
    () => ({
      columns,
      rows: transactions,
    }),
    [columns, transactions]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDBox>
                  <MDTypography variant="h6" color="white">
                    Payment Transactions
                  </MDTypography>
                  <MDTypography variant="caption" color="white" opacity={0.85}>
                    Track payments linked to travel orders and invoices
                  </MDTypography>
                </MDBox>
                <MDButton
                  variant="gradient"
                  color="dark"
                  onClick={() => setIsModalOpen(true)}
                  disabled={!canCreate}
                >
                  <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                  &nbsp;New Transaction
                </MDButton>
              </MDBox>

              <MDBox pt={3}>
                {isLoading ? (
                  <MDBox py={6} display="flex" justifyContent="center">
                    <PearlLogoLoader size="md" message="Loading payment transactions..." />
                  </MDBox>
                ) : transactions.length > 0 ? (
                  <DataTable
                    table={tableData}
                    isSorted
                    entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                    canSearch
                    showTotalEntries
                    noEndBorder
                    pagination={{ variant: "gradient", color: "info" }}
                  />
                ) : (
                  <EmptyState moduleName="Payment Transaction" canCreate={canCreate} />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <NewPaymentTransactionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveTransaction}
      />

      <ConfirmDeleteModal
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, id: null })}
        onConfirm={handleDelete}
        title="Delete Payment Transaction"
        message="Are you sure you want to delete this payment transaction? Related invoice and order payment status will be recalculated."
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default PaymentTransactions;
